import {
  Auth0AccountRole,
  OrganizationResponseForAdmins,
  OrganizationsService,
} from "client/openapi";
import { ErrorBlock, LoadingBlock } from "components/StatusBlock";
import { ReactNode, useContext, useEffect, useState } from "react";
import { APIResponse, PageStatus } from "types";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { useOrganizationRenamed } from "util/organizationRenamed";
import "./index.css";

/**
 * Shell for the admin-only organization pages: refuses anyone who is not
 * an admin and gives the page the selected organization's id at once, so
 * a page that needs only the id (the forms pages) never waits for the
 * whole organization.
 */
export function OrgAdminShell({
  children,
}: {
  children: (orgId: number) => ReactNode;
}) {
  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);

  if (currently_selected_role !== Auth0AccountRole.ORG_ADMIN) {
    return (
      <ErrorBlock
        status={PageStatus.ERROR}
        title="Unauthorized"
        message="You do not have permission to access this page."
      />
    );
  }

  return (
    <div className="container-fluid mx-10 page--student-data pt-8">
      {currently_selected_organization ? (
        children(currently_selected_organization)
      ) : (
        <LoadingBlock status={PageStatus.LOADING} />
      )}
    </div>
  );
}

/**
 * The shell for the pages that need the whole organization (Financials,
 * CRM, Classes): loads the selected organization, then shows the page.
 */
export default function OrgAdminPage({
  children,
}: {
  children: (organization: OrganizationResponseForAdmins) => ReactNode;
}) {
  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );

  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<APIResponse>();
  const [organization, setOrganization] =
    useState<OrganizationResponseForAdmins>();
  // The pages below (chart file names) follow a rename.
  useOrganizationRenamed(({ id, name }) =>
    setOrganization((org) => (org?.id === id ? { ...org, name } : org))
  );

  useEffect(() => {
    if (currently_selected_organization) {
      OrganizationsService.getOrganization({
        orgId: currently_selected_organization,
      })
        .then((org) => {
          setStatus(PageStatus.SUCCESS);
          setOrganization(org as OrganizationResponseForAdmins);
        })
        .catch((err) => {
          setStatus(PageStatus.ERROR);
          setError({ message: "Could not get org" });
          console.error(`Error ${err}`);
        });
    }
  }, [currently_selected_organization]);

  return (
    <OrgAdminShell>
      {() => (
        <>
          <LoadingBlock status={status} />
          <ErrorBlock status={status} message={error?.message} />
          {organization && children(organization)}
        </>
      )}
    </OrgAdminShell>
  );
}
